/**
 * Admin bake-off UI: side-by-side label renders with full-size lightbox compare.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { adminApi } from '../../api/client';
import { useImpersonationStore } from '../../store/impersonationStore';

const SAMPLE_ZPL = `^XA
^PW812
^LL1218
^FO50,50^A0N,40,40^FDRenderer Bake-off^FS
^FO50,120^A0N,28,28^FDSide-by-side compare^FS
^FO50,180^BY2^BCN,80,Y,N,N^FD00012345678901234567^FS
^XZ
`;

type ProviderId = 'labelary' | 'stand-in' | 'binarykits' | 'zpl-js';

type ProviderResult = {
  id: string;
  name: string;
  role: string;
  ok: boolean;
  latencyMs: number;
  contentType?: string;
  imageBase64?: string;
  outputHash?: string;
  pages?: number;
  error?: string;
  unsupported?: boolean;
  notes?: string[];
  available: boolean;
};

const PROVIDER_OPTIONS: { id: ProviderId; label: string }[] = [
  { id: 'labelary', label: 'Labelary (oracle)' },
  { id: 'stand-in', label: 'Local stand-in' },
  { id: 'binarykits', label: 'BinaryKits.Zpl' },
  { id: 'zpl-js', label: 'zpl-js' },
];

function imgSrc(p: ProviderResult): string | null {
  if (!p.imageBase64) return null;
  return `data:${p.contentType || 'image/png'};base64,${p.imageBase64}`;
}

export function AdminRendererComparePage() {
  const { selectedTenantId } = useImpersonationStore();
  const [mode, setMode] = useState<'zpl' | 'template'>('zpl');
  const [zpl, setZpl] = useState(SAMPLE_ZPL);
  const [templateKey, setTemplateKey] = useState('');
  const [jsonPayload, setJsonPayload] = useState(
    JSON.stringify(
      {
        shipToName: 'Store #100',
        poNumber: 'PO-12345',
        sscc: '00012345678901234567',
      },
      null,
      2
    )
  );
  const [dpi, setDpi] = useState(203);
  const [selectedProviders, setSelectedProviders] = useState<ProviderId[]>([
    'labelary',
    'binarykits',
    'zpl-js',
    'stand-in',
  ]);
  const [error, setError] = useState<string | null>(null);

  /** Multi-select for lightbox compare (click cards to toggle) */
  const [pickedIds, setPickedIds] = useState<string[]>([]);
  /** Lightbox open with these providers (full size, no card shrink) */
  const [lightboxIds, setLightboxIds] = useState<string[] | null>(null);

  const compareMutation = useMutation({
    mutationFn: async () => {
      if (mode === 'zpl') {
        return adminApi.compareRenderers({
          zpl,
          dpi,
          providers: selectedProviders,
        });
      }
      const data = JSON.parse(jsonPayload) as Record<string, unknown>;
      if (!templateKey.trim()) {
        throw new Error('Template key is required');
      }
      if (!selectedTenantId) {
        throw new Error('Select a tenant (header switcher) when generating from a template');
      }
      return adminApi.compareRenderers({
        templateKey: templateKey.trim(),
        data,
        tenantId: selectedTenantId,
        dpi,
        providers: selectedProviders,
      });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Compare failed');
    },
    onSuccess: () => {
      setError(null);
      setPickedIds([]);
      setLightboxIds(null);
    },
  });

  const results = compareMutation.data;
  const providers = (results?.providers || []) as ProviderResult[];

  const providerById = useMemo(() => {
    const m = new Map<string, ProviderResult>();
    providers.forEach((p) => m.set(p.id, p));
    return m;
  }, [providers]);

  const lightboxProviders = useMemo(() => {
    if (!lightboxIds) return [];
    return lightboxIds
      .map((id) => providerById.get(id))
      .filter((p): p is ProviderResult => !!p);
  }, [lightboxIds, providerById]);

  useEffect(() => {
    if (!lightboxIds) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIds(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIds]);

  const winnerHint = useMemo(() => {
    if (!providers.length) return null;
    const withImage = providers.filter((p) => p.ok && p.imageBase64);
    if (withImage.length === 0) {
      return 'No raster previews yet — check Labelary / BinaryKits / zpl-js errors.';
    }
    return 'Click an image for full size. Select 2+ cards (checkboxes or click) then “Compare selected full size”.';
  }, [providers]);

  const toggleProvider = (id: ProviderId) => {
    setSelectedProviders((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const togglePick = (id: string) => {
    setPickedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openSingle = (p: ProviderResult) => {
    if (p.imageBase64) {
      setLightboxIds([p.id]);
    }
  };

  const openPicked = () => {
    const withImg = pickedIds.filter((id) => providerById.get(id)?.imageBase64);
    if (withImg.length === 0) return;
    setLightboxIds(withImg);
  };

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Renderer bake-off</h1>
        <p className="mt-1 text-sm text-gray-600">
          Compare Labelary (oracle) with BinaryKits.Zpl and zpl-js (Zebrash) at the same label size
          (default 4×6&quot;). DPI maps to printer density (203→8dpmm, 300→12dpmm) for all engines.
          Click a preview for full size; select two or more for a side-by-side popup.
        </p>
      </div>

      <div className="bg-white rounded-lg border shadow-sm p-6 space-y-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex rounded-lg border overflow-hidden">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${
                mode === 'zpl' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700'
              }`}
              onClick={() => setMode('zpl')}
            >
              Paste ZPL
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium ${
                mode === 'template' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700'
              }`}
              onClick={() => setMode('template')}
            >
              Template + data
            </button>
          </div>

          <label className="text-sm text-gray-700 flex items-center gap-2">
            DPI
            <select
              className="border rounded px-2 py-1"
              value={dpi}
              onChange={(e) => setDpi(Number(e.target.value))}
            >
              {[152, 203, 300, 600].map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap gap-3">
          {PROVIDER_OPTIONS.map((p) => (
            <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={selectedProviders.includes(p.id)}
                onChange={() => toggleProvider(p.id)}
              />
              {p.label}
            </label>
          ))}
        </div>

        {mode === 'zpl' ? (
          <textarea
            className="w-full h-48 font-mono text-xs border rounded-lg p-3"
            value={zpl}
            onChange={(e) => setZpl(e.target.value)}
            spellCheck={false}
          />
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template key
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={templateKey}
                onChange={(e) => setTemplateKey(e.target.value)}
                placeholder="e.g. buc-ees-shipping"
              />
              <p className="mt-1 text-xs text-gray-500">
                Tenant: {selectedTenantId || '(select via Client View tenant switcher, then return)'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label data (JSON)
              </label>
              <textarea
                className="w-full h-40 font-mono text-xs border rounded-lg p-3"
                value={jsonPayload}
                onChange={(e) => setJsonPayload(e.target.value)}
                spellCheck={false}
              />
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={compareMutation.isPending || selectedProviders.length === 0}
            onClick={() => compareMutation.mutate()}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {compareMutation.isPending ? 'Comparing…' : 'Compare renderers'}
          </button>
          {results && (
            <button
              type="button"
              disabled={pickedIds.filter((id) => providerById.get(id)?.imageBase64).length < 1}
              onClick={openPicked}
              className="px-4 py-2 border border-primary-600 text-primary-700 text-sm font-medium rounded-lg hover:bg-primary-50 disabled:opacity-40"
            >
              Compare selected full size
              {pickedIds.length > 0 ? ` (${pickedIds.length})` : ''}
            </button>
          )}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </div>

      {results && (
        <div className="space-y-4">
          <div className="bg-slate-50 border rounded-lg p-4 text-sm text-gray-700">
            <div className="flex flex-wrap gap-4">
              <span>
                <strong>ZPL hash:</strong>{' '}
                <code className="text-xs">{results.zplHash.slice(0, 16)}…</code>
              </span>
              <span>
                <strong>Labels:</strong> {results.labelCount}
              </span>
              <span>
                <strong>At:</strong> {new Date(results.comparedAt).toLocaleString()}
              </span>
            </div>
            {winnerHint && <p className="mt-2 text-primary-800">{winnerHint}</p>}
          </div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-4">
            {providers.map((p) => {
              const src = imgSrc(p);
              const picked = pickedIds.includes(p.id);
              return (
                <div
                  key={p.id}
                  className={`bg-white border rounded-lg shadow-sm overflow-hidden flex flex-col ${
                    picked
                      ? 'ring-2 ring-primary-500 border-primary-400'
                      : p.ok
                        ? 'border-green-200'
                        : p.available
                          ? 'border-amber-200'
                          : 'border-gray-200'
                  }`}
                >
                  <div className="px-4 py-3 border-b bg-gray-50">
                    <div className="flex items-center justify-between gap-2">
                      <label className="flex items-center gap-2 min-w-0">
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => togglePick(p.id)}
                          disabled={!p.imageBase64}
                          title={p.imageBase64 ? 'Select for full-size compare' : 'No image'}
                        />
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{p.name}</h3>
                      </label>
                      <span
                        className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                          p.role === 'oracle'
                            ? 'bg-blue-100 text-blue-800'
                            : p.role === 'candidate'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {p.role}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {p.latencyMs} ms
                      {p.outputHash && (
                        <>
                          {' '}
                          · hash {p.outputHash.slice(0, 10)}…
                        </>
                      )}
                    </div>
                  </div>

                  <div className="p-3 flex-1 flex flex-col items-center justify-center min-h-[280px] bg-slate-100">
                    {src ? (
                      <button
                        type="button"
                        onClick={() => openSingle(p)}
                        className="group relative w-full flex justify-center focus:outline-none"
                        title="Click for full size"
                      >
                        <img
                          src={src}
                          alt={`${p.name} preview`}
                          className="max-w-full max-h-80 object-contain border border-gray-300 bg-white shadow-sm group-hover:shadow-md transition-shadow"
                        />
                        <span className="absolute bottom-2 right-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded opacity-0 group-hover:opacity-100">
                          Full size
                        </span>
                      </button>
                    ) : p.ok ? (
                      <div className="text-center text-sm text-gray-600 space-y-2">
                        <p className="font-medium text-green-700">OK (no raster)</p>
                        <p className="text-xs">Use output hash for determinism checks.</p>
                      </div>
                    ) : (
                      <div className="text-center text-sm space-y-1 px-2">
                        <p className="font-medium text-red-600">
                          {p.unsupported ? 'Unsupported command' : 'Failed / unavailable'}
                        </p>
                        <p className="text-xs text-gray-600 break-words">{p.error}</p>
                      </div>
                    )}
                  </div>

                  {p.notes && p.notes.length > 0 && (
                    <ul className="px-4 py-2 border-t text-xs text-gray-500 list-disc list-inside">
                      {p.notes.map((n) => (
                        <li key={n}>{n}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>

          <details className="bg-white border rounded-lg p-4">
            <summary className="text-sm font-medium cursor-pointer">ZPL preview</summary>
            <pre className="mt-2 text-xs font-mono whitespace-pre-wrap text-gray-700 max-h-48 overflow-auto">
              {results.zplPreview}
            </pre>
          </details>
        </div>
      )}

      {/* Full-size lightbox — does not constrain to card height */}
      {lightboxIds && lightboxProviders.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-black/80"
          role="dialog"
          aria-modal="true"
          aria-label="Full size renderer compare"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-black/40 text-white shrink-0">
            <div className="text-sm font-medium">
              Full size
              {lightboxProviders.length > 1
                ? ` — ${lightboxProviders.map((p) => p.name).join(' · ')}`
                : ` — ${lightboxProviders[0].name}`}
            </div>
            <button
              type="button"
              className="px-3 py-1.5 rounded bg-white/15 hover:bg-white/25 text-sm"
              onClick={() => setLightboxIds(null)}
            >
              Close (Esc)
            </button>
          </div>
          <div
            className={`flex-1 overflow-auto p-4 grid gap-4 ${
              lightboxProviders.length === 1
                ? 'grid-cols-1 place-items-center'
                : lightboxProviders.length === 2
                  ? 'grid-cols-1 md:grid-cols-2'
                  : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
            }`}
          >
            {lightboxProviders.map((p) => {
              const src = imgSrc(p);
              return (
                <div
                  key={p.id}
                  className="flex flex-col items-center bg-white rounded-lg overflow-hidden shadow-xl min-h-0"
                >
                  <div className="w-full px-3 py-2 border-b text-sm font-semibold text-gray-800 flex justify-between">
                    <span>{p.name}</span>
                    <span className="text-xs font-normal text-gray-500">{p.latencyMs} ms</span>
                  </div>
                  <div className="p-4 w-full overflow-auto flex justify-center bg-slate-200">
                    {src ? (
                      <img
                        src={src}
                        alt={`${p.name} full size`}
                        className="max-w-none w-auto h-auto shadow-md bg-white"
                        style={{ imageRendering: 'auto' }}
                      />
                    ) : (
                      <p className="text-sm text-gray-600">No image</p>
                    )}
                  </div>
                  {p.outputHash && (
                    <div className="w-full px-3 py-1.5 border-t text-[10px] font-mono text-gray-500 truncate">
                      {p.outputHash}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
